import { useCallback, useEffect, useState } from 'react';
import type { CartItem } from '../types/shipping';

const CART_STORAGE_KEY = 'cartItems';

// Llegeix el carret directament de localStorage (sense passar per l'estat de
// React), perquè les mutacions puguin partir sempre de la versió més
// recent i no ressuscitin articles esborrats des d'un altre component.
function readCartFromStorage(): CartItem[] {
  const raw = localStorage.getItem(CART_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? normalizeCartItems(parsed) : [];
  } catch {
    return [];
  }
}

// Descarta entrades invàlides i agrupa les repetides: un carret amb el mateix
// productId en dues línies inflaria el recompte d'ampolles i, en enviar-lo a
// WooCommerce, també el cost d'enviament (WooCommerce suma les quantitats dels
// product_id repetits). Millor sanejar-ho a l'entrada.
function normalizeCartItems(parsed: unknown[]): CartItem[] {
  const byProductId = new Map<number, CartItem>();

  for (const entry of parsed) {
    const item = entry as Partial<CartItem>;
    const productId = Number(item?.productId);
    const quantity = Number(item?.quantity);

    if (!Number.isFinite(productId) || !Number.isFinite(quantity) || quantity <= 0) {
      continue;
    }

    const existing = byProductId.get(productId);
    if (existing) {
      existing.quantity += quantity;
    } else {
      byProductId.set(productId, { productId, quantity });
    }
  }

  return [...byProductId.values()];
}

function writeCartToStorage(items: CartItem[]) {
  if (items.length === 0) {
    localStorage.removeItem(CART_STORAGE_KEY);
  } else {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }
  // Notifiquem la resta de components (CartIcon, Products, Cart...) del canvi.
  window.dispatchEvent(new Event('storageUpdated'));
}

/**
 * Font de veritat única pel carret. Tots els components han de llegir i
 * modificar el carret a través d'aquest hook en lloc de tocar localStorage
 * directament, així s'evita que es quedin desincronitzats entre ells.
 */
export function useCartItems() {
  const [items, setItems] = useState<CartItem[]>(() => readCartFromStorage());

  useEffect(() => {
    const reload = () => setItems(readCartFromStorage());

    // 'storage' arriba quan altres pestanyes/finestres toquen localStorage;
    // 'storageUpdated' és el nostre event intern per als canvis dins la mateixa pàgina.
    window.addEventListener('storage', reload);
    window.addEventListener('storageUpdated', reload);

    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('storageUpdated', reload);
    };
  }, []);

  // Totes les mutacions passen per aquí: es llegeix l'estat més recent de
  // localStorage, s'hi aplica el canvi i s'escriu de nou.
  const mutate = useCallback((updater: (current: CartItem[]) => CartItem[]) => {
    const current = readCartFromStorage();
    const next = updater(current);
    writeCartToStorage(next);
    setItems(next);
  }, []);

  const setQuantity = useCallback((productId: number, quantity: number) => {
    mutate(current => {
      if (quantity <= 0) {
        return current.filter(item => item.productId !== productId);
      }
      const exists = current.some(item => item.productId === productId);
      if (exists) {
        return current.map(item => item.productId === productId ? { ...item, quantity } : item);
      }
      return [...current, { productId, quantity }];
    });
  }, [mutate]);

  const addOne = useCallback((productId: number) => {
    mutate(current => {
      const exists = current.find(item => item.productId === productId);
      if (exists) {
        return current.map(item => item.productId === productId ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...current, { productId, quantity: 1 }];
    });
  }, [mutate]);

  const removeItem = useCallback((productId: number) => {
    mutate(current => current.filter(item => item.productId !== productId));
  }, [mutate]);

  const clear = useCallback(() => {
    mutate(() => []);
  }, [mutate]);

  const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return { items, setQuantity, addOne, removeItem, clear, itemsCount };
}
